import { memo, useCallback, useId } from "react";

import { stringToBoolean } from "@/lib/chillnn/string-to-boolean";

import { ExternalLink } from "@/components/ui/button/external-link";
import { FormItem } from "@/components/ui/form/_base/form";
import { Checkbox } from "@/components/ui/input/checkbox";

import { FormFieldLayout } from "../../layouts";

import type { CheckedState } from "@radix-ui/react-checkbox";

type Props = {
	isPlanDiscountEligible: boolean;
	isRoomDiscountEligible: boolean;
	isOptionDiscountEligible: boolean;
	isAdditionalFeeDiscountEligible: boolean;
	onChangePlanDiscountEligible: (value: boolean) => void;
	onChangeRoomDiscountEligible: (value: boolean) => void;
	onChangeOptionDiscountEligible: (value: boolean) => void;
	onChangeAdditionalFeeDiscountEligible: (value: boolean) => void;
};

const NOTION_URL = "https://chillnn.notion.site/2a1fffe15c578051854fffb4aafa3fd9";

export const DiscountEligibleComposition = memo(
	({
		isPlanDiscountEligible,
		isRoomDiscountEligible,
		isOptionDiscountEligible,
		isAdditionalFeeDiscountEligible,
		onChangePlanDiscountEligible,
		onChangeRoomDiscountEligible,
		onChangeOptionDiscountEligible,
		onChangeAdditionalFeeDiscountEligible,
	}: Props) => {
		const planDiscountId = useId();
		const roomDiscountId = useId();
		const optionDiscountId = useId();
		const additionalFeeDiscountId = useId();

		/**
		 * プランディスカウント適用可能の変更
		 */
		const handleChangePlanDiscountEligible = useCallback(
			(input: CheckedState) => {
				const value = typeof input === "string" ? stringToBoolean(input) : input;
				onChangePlanDiscountEligible(value);
			},
			[onChangePlanDiscountEligible],
		);

		/**
		 * 部屋ディスカウント適用可能の変更
		 */
		const handleChangeRoomDiscountEligible = useCallback(
			(input: CheckedState) => {
				const value = typeof input === "string" ? stringToBoolean(input) : input;
				onChangeRoomDiscountEligible(value);
			},
			[onChangeRoomDiscountEligible],
		);

		/**
		 * オプションディスカウント適用可能の変更
		 */
		const handleChangeOptionDiscountEligible = useCallback(
			(input: CheckedState) => {
				const value = typeof input === "string" ? stringToBoolean(input) : input;
				onChangeOptionDiscountEligible(value);
			},
			[onChangeOptionDiscountEligible],
		);

		/**
		 * 追加料金ディスカウント適用可能の変更
		 */
		const handleChangeAdditionalFeeDiscountEligible = useCallback(
			(input: CheckedState) => {
				const value = typeof input === "string" ? stringToBoolean(input) : input;
				onChangeAdditionalFeeDiscountEligible(value);
			},
			[onChangeAdditionalFeeDiscountEligible],
		);

		return (
			<FormFieldLayout title="ディスカウントの対象">
				<div className="space-y-4">
					<div className="space-y-3">
						<FormItem>
							<div className="space-y-1">
								<div className="flex items-center gap-x-3">
									<Checkbox
										defaultChecked={isPlanDiscountEligible}
										checked={isPlanDiscountEligible}
										onCheckedChange={handleChangePlanDiscountEligible}
										id={planDiscountId}
									/>
									<label className="font-normal" htmlFor={planDiscountId}>
										プラン
									</label>
								</div>
								{isPlanDiscountEligible && (
									<p className="pl-8 text-sm text-zinc-600">
										このディスカウントをプラン料金に適用する
									</p>
								)}
							</div>
						</FormItem>

						<FormItem>
							<div className="space-y-1">
								<div className="flex items-center gap-x-3">
									<Checkbox
										defaultChecked={isRoomDiscountEligible}
										checked={isRoomDiscountEligible}
										onCheckedChange={handleChangeRoomDiscountEligible}
										id={roomDiscountId}
									/>
									<label className="font-normal" htmlFor={roomDiscountId}>
										部屋
									</label>
								</div>
								{isRoomDiscountEligible && (
									<p className="pl-8 text-sm text-zinc-600">このディスカウントを部屋料金に適用する</p>
								)}
							</div>
						</FormItem>

						<FormItem>
							<div className="space-y-1">
								<div className="flex items-center gap-x-3">
									<Checkbox
										defaultChecked={isOptionDiscountEligible}
										checked={isOptionDiscountEligible}
										onCheckedChange={handleChangeOptionDiscountEligible}
										id={optionDiscountId}
									/>
									<label className="font-normal" htmlFor={optionDiscountId}>
										オプション
									</label>
								</div>
								{isOptionDiscountEligible && (
									<p className="pl-8 text-sm text-zinc-600">
										このディスカウントをオプション料金に適用する
									</p>
								)}
							</div>
						</FormItem>

						<FormItem>
							<div className="space-y-1">
								<div className="flex items-center gap-x-3">
									<Checkbox
										defaultChecked={isAdditionalFeeDiscountEligible}
										checked={isAdditionalFeeDiscountEligible}
										onCheckedChange={handleChangeAdditionalFeeDiscountEligible}
										id={additionalFeeDiscountId}
									/>
									<label className="font-normal" htmlFor={additionalFeeDiscountId}>
										追加料金（宿泊税など）
									</label>
								</div>
								{isAdditionalFeeDiscountEligible && (
									<p className="pl-8 text-sm text-zinc-600">
										このディスカウントを追加料金（宿泊税など）に適用する
									</p>
								)}
							</div>
						</FormItem>
					</div>
					<p className="whitespace-pre-wrap text-sm text-caption-50">
						Q. 特定のプランや部屋、オプションをディスカウントの対象から外すことはできますか？
						<br />
						A. はい、できます。それぞれの編集画面で適用対象から除外してください。詳細は
						<ExternalLink href={NOTION_URL} text="こちら" className="text-sm" />
					</p>
				</div>
			</FormFieldLayout>
		);
	},
);
